import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "./api/api";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

/**
 * Fetch user profile
 */
const fetchUserProfile = async (token) => {
  if (!token) return null;

  try {
    const res = await api.get("/api/user-profile/", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (err) {
    console.error("[Auth] Profile fetch failed:", err?.response?.status);
    return null;
  }
};

/**
 * Refresh access token
 */
const refreshAccessToken = async () => {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) return null;

  try {
    const res = await api.post("/api/token/refresh/", {
      refresh,
    });

    const access = res.data?.access;

    if (access) {
      localStorage.setItem("access", access);
      return access;
    }

    return null;
  } catch (err) {
    console.error("[Auth] Refresh failed:", err?.response?.status);
    return null;
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  /**
   * Load user safely (with refresh fallback)
   */
  const loadUser = useCallback(async () => {
    setInitializing(true);

    let token = localStorage.getItem("access");

    // If no access token, try refresh
    if (!token) {
      token = await refreshAccessToken();
    }

    // Still no token → stop here (NO LOOP)
    if (!token) {
      setUser(null);
      setInitializing(false);
      setAuthReady(true);
      return;
    }

    const profile = await fetchUserProfile(token);

    if (!profile) {
      // Only try refresh ONCE more (prevents loop)
      const newToken = await refreshAccessToken();

      if (newToken) {
        const retryProfile = await fetchUserProfile(newToken);
        setUser(retryProfile || null);
      } else {
        setUser(null);
      }
    } else {
      setUser(profile);
    }

    setInitializing(false);
    setAuthReady(true);
  }, []);

  /**
   * Initial auth bootstrap (RUNS ONCE)
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Silent refresh checker
   */
  useEffect(() => {
    const interval = setInterval(async () => {
      const token = localStorage.getItem("access");
      if (!token) return;

      try {
        const decoded = jwtDecode(token);
        const exp = decoded.exp * 1000;

        if (Date.now() > exp - 60000) {
          await refreshAccessToken();
        }
      } catch (err) {
        console.error("[Auth] Token decode error:", err);
      }
    }, 4 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Safe logout (NO redirect loop)
   */
  const logout = async () => {
    const refresh = localStorage.getItem("refresh");

    try {
      if (refresh) {
        await api.post("/api/logout/", {
          refresh_token: refresh,
        });
      }
    } catch (err) {
      console.error("[Auth] Logout API error:", err);
    }

    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("user");

    setUser(null);

    // IMPORTANT: do NOT hard reload or you create loop
    window.location.href = "/login";
  };

  /**
   * External auth refresh trigger
   */
  const refreshUser = useCallback(() => {
    loadUser();
  }, [loadUser]);

  const value = {
    user,
    setUser,
    logout,
    initializing,
    authReady,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };






// // src/AuthContext.js
// import React, { createContext, useState, useEffect, useCallback } from "react";
// import api from "./api/api";
// import { jwtDecode } from "jwt-decode"; // Add this package if not installed: npm install jwt-decode

// const AuthContext = createContext();

// /**
//  * Fetch current user profile using access token (if present).
//  */
// const fetchUserProfile = async () => {
//   const token = localStorage.getItem("access");
//   if (!token) return null;

//   try {
//     const res = await api.get("/api/user-profile/", {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     return res.data;
//   } catch (err) {
//     console.error("[AuthContext] Failed to fetch user profile:", err);
//     return null;
//   }
// };

// /**
//  * Refresh access token silently using refresh token.
//  */
// const refreshAccessToken = async () => {
//   const refresh = localStorage.getItem("refresh");
//   if (!refresh) return null;

//   try {
//     const res = await api.post("/api/token/refresh/", { refresh });
//     const { access } = res.data;
//     if (access) {
//       localStorage.setItem("access", access);
//       console.log("[AuthContext] Access token refreshed.");
//       return access;
//     }
//   } catch (err) {
//     console.error("[AuthContext] Silent token refresh failed:", err);
//     return null;
//   }
//   return null;
// };

// const AuthProvider = ({ children }) => {
//   const [user, setUser] = useState(null);
//   const [initializing, setInitializing] = useState(true);

//   const loadUser = useCallback(async () => {
//     setInitializing(true);
//     const profile = await fetchUserProfile();
//     setUser(profile);
//     setInitializing(false);
//   }, []);

//   // Load user on mount
//   useEffect(() => {
//     loadUser();
//   }, [loadUser]);

//   // Silent token refresh interval
//   useEffect(() => {
//     const interval = setInterval(async () => {
//       const token = localStorage.getItem("access");
//       if (!token) return;

//       try {
//         const decoded = jwtDecode(token);
//         const expTime = decoded.exp * 1000;
//         const currentTime = Date.now();

//         // If token will expire in the next 60 seconds, refresh it
//         if (expTime - currentTime < 60 * 1000) {
//           await refreshAccessToken();
//         }
//       } catch (err) {
//         console.error("[AuthContext] Error decoding token:", err);
//       }
//     }, 4 * 60 * 1000); // Check every 4 minutes

//     return () => clearInterval(interval);
//   }, []);

//   // Listen for auth updates (login, token rotation)
//   useEffect(() => {
//     const onAuthUpdated = () => loadUser();
//     const onForceLogout = () => {
//       setUser(null);
//       window.location.href = "/login";
//     };

//     window.addEventListener("auth-updated", onAuthUpdated);
//     window.addEventListener("force-logout", onForceLogout);

//     return () => {
//       window.removeEventListener("auth-updated", onAuthUpdated);
//       window.removeEventListener("force-logout", onForceLogout);
//     };
//   }, [loadUser]);

//   const logout = async () => {
//     const refresh = localStorage.getItem("refresh");
//     try {
//       if (refresh) {
//         await api.post("/api/logout/", { refresh_token: refresh });
//       }
//     } catch (err) {
//       console.error("[AuthContext] Logout error:", err);
//     } finally {
//       localStorage.removeItem("access");
//       localStorage.removeItem("refresh");
//       localStorage.removeItem("user");
//       setUser(null);
//       window.location.href = "/login";
//     }
//   };

//   const value = {
//     user,
//     setUser,
//     logout,
//     initializing,
//     refreshUser: loadUser,
//   };

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// };

// export { AuthContext, AuthProvider };


